import DOMPurify from 'dompurify'
import queryString from 'query-string'

import { log } from 'src/services/app/log.service'

/**
 * Security validation helper functions
 * Centralized location for security-critical validation logic
 */

/**
 * Coerce a Vue Router query value into a cleaned invite token, or null when missing/invalid.
 * Round-trips through query-string (same sanitize pattern as util.helper query helpers).
 * @param {string|string[]|null|undefined} raw - `route.query.token` (or equivalent)
 * @returns {string|null}
 */
export function sanitizeInviteToken(raw) {
  if (raw === null || raw === undefined) {
    return null
  }

  let value = Array.isArray(raw) ? raw[0] : raw
  if (value === null || value === undefined) {
    return null
  }

  const parsed = queryString.parse(queryString.stringify({ token: value })).token
  value = Array.isArray(parsed) ? parsed[0] : parsed

  return typeof value === 'string' && value.length > 0 ? value : null
}

/**
 * Validate redirect route to prevent open redirect vulnerabilities
 * @param {string|null} route - Route to validate
 * @returns {boolean} - True if valid, false if invalid
 * @description Only accepts relative paths starting with '/' to prevent external redirects
 */
export function isValidRedirectRoute(route) {
  // Null is valid (clears redirect)
  if (route === null || route === undefined) {
    return true
  }

  // Must be a string
  if (typeof route !== 'string') {
    log.error('isValidRedirectRoute: Route must be a string', {
      route,
      type: typeof route,
    })
    return false
  }

  // Must start with '/' (relative path only)
  if (!route.startsWith('/')) {
    log.error('isValidRedirectRoute: Route must start with / (relative path)', {
      route,
    })
    return false
  }

  // Prevent protocol-relative URLs (//evil.com)
  if (route.startsWith('//')) {
    log.error('isValidRedirectRoute: Protocol-relative URLs not allowed', {
      route,
    })
    return false
  }

  // Prevent backslash bypasses (\\ or \/)
  if (route.includes('\\')) {
    log.error('isValidRedirectRoute: Backslashes not allowed in routes', {
      route,
    })
    return false
  }

  // Prevent absolute URL patterns (http://, https://, etc.)
  if (route.includes('://')) {
    log.error('isValidRedirectRoute: Absolute URL patterns not allowed', {
      route,
    })
    return false
  }

  return true
}

/**
 * Sanitize HTML to allow only safe formatting tags
 * @param {string} html - HTML string to sanitize
 * @returns {string} - Sanitized HTML safe for rendering
 * @description Strict allowlist: only permits <mark>, <b>, <strong>, <em>, <i> tags with no attributes
 */
export function sanitizeHtml(html) {
  if (!html || typeof html !== 'string') {
    return ''
  }

  // Strict configuration: only allow safe formatting tags
  const config = {
    ALLOWED_TAGS: ['mark', 'b', 'strong', 'em', 'i'],
    ALLOWED_ATTR: [], // No attributes allowed (no class, no style, nothing)
    KEEP_CONTENT: true, // Keep text content even if tags are removed
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_TRUSTED_TYPE: false,
  }

  try {
    const sanitized = DOMPurify.sanitize(html, config)
    return sanitized
  } catch (error) {
    log.error('sanitizeHtml: Sanitization failed', { html, error })
    // Return plain text on error (strip all HTML)
    return html.replace(/<[^>]*>/g, '')
  }
}

const TERMS_ALLOWED_TAGS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'ul',
  'ol',
  'li',
  'strong',
  'em',
  'b',
  'i',
  'blockquote',
  'hr',
  'br',
  'pre',
  'code',
  'a',
]

/**
 * Sanitize HTML for legal/terms content with a structural allowlist
 * @param {string} html - HTML string to sanitize
 * @returns {string} - Sanitized HTML safe for rendering
 * @description Broader allowlist for legal documents: headings, lists, links (http/https only), etc.
 */
export function sanitizeTermsHtml(html) {
  if (!html || typeof html !== 'string') {
    return ''
  }

  const config = {
    ALLOWED_TAGS: TERMS_ALLOWED_TAGS,
    ALLOWED_ATTR: ['href'],
    FORBID_ATTR: ['style', 'class'],
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_TRUSTED_TYPE: false,
  }

  const afterSanitizeAttributesHook = (node) => {
    if (node.tagName !== 'A') {
      return
    }

    const href = node.getAttribute('href')
    if (!href) {
      return
    }

    try {
      const urlObj = new URL(href, 'https://example.com')
      if (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') {
        node.setAttribute('rel', 'noopener noreferrer')
        node.setAttribute('target', '_blank')
      } else {
        node.removeAttribute('href')
      }
    } catch {
      node.removeAttribute('href')
    }
  }

  try {
    DOMPurify.addHook('afterSanitizeAttributes', afterSanitizeAttributesHook)
    const sanitized = DOMPurify.sanitize(html, config)
    return sanitized
  } catch (error) {
    log.error('sanitizeTermsHtml: Sanitization failed', { html, error })
    return html.replace(/<[^>]*>/g, '')
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes', afterSanitizeAttributesHook)
  }
}

/**
 * Sanitize request payload for error reporting (PostHog/Slack).
 * Redacts sensitive fields, skips FormData (files can't be serialized).
 * @param {Object|FormData|null} data - Request payload
 * @returns {Object|undefined} - Sanitized payload or undefined if nothing to report
 */
const SENSITIVE_KEYS = [
  'account_id',
  'token',
  'text',
  'problem_description',
  'ipAddress',
  'userAgent',
  'ip_address',
  'user_agent',
]

export function sanitizePayload(data, deep = false) {
  if (data === null) return null
  if (data === undefined) return undefined
  if (data instanceof FormData) return 'Form Data'

  let parsed = data
  if (typeof data === 'string') {
    try {
      parsed = JSON.parse(data)
    } catch {
      return undefined
    }
  }

  if (typeof parsed !== 'object') return parsed
  if (Array.isArray(parsed)) {
    return deep ? parsed.map((item) => sanitizePayload(item, true)) : parsed
  }

  const sanitized = {}
  for (const [key, value] of Object.entries(parsed)) {
    if (SENSITIVE_KEYS.includes(key)) {
      sanitized[key] = _redact(value)
    } else if (deep && value && typeof value === 'object') {
      sanitized[key] = sanitizePayload(value, true)
    } else {
      sanitized[key] = value
    }
  }
  return sanitized
}

function _redact(value) {
  if (typeof value !== 'string' || value.length <= 6) return '[REDACTED]'
  return `${value.slice(0, 3)}...${value.slice(-3)}`
}

const DOMAIN_REGEX = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i

/**
 * Shared hostname validation after {@link validateUrlProtocol}.
 * @param {string} value
 * @param {{ allowPath: boolean }} options - `false` = bare hostname only (domain field); `true` = path/query/hash allowed
 * @returns {boolean}
 */
function _isValidHostnameUrl(value, { allowPath }) {
  const validation = validateUrlProtocol(value)
  if (!validation.isValid) {
    return false
  }

  try {
    const urlObj = new URL(validation.normalizedUrl)
    if (urlObj.username || urlObj.password) {
      return false
    }

    if (!allowPath) {
      const hasExtraComponents =
        urlObj.port || (urlObj.pathname && urlObj.pathname !== '/') || urlObj.search || urlObj.hash
      if (hasExtraComponents) {
        return false
      }
    }

    return DOMAIN_REGEX.test(urlObj.hostname)
  } catch {
    return false
  }
}

/**
 * Validate that a string is a real-looking domain AND passes URL protocol security checks.
 * Stricter than validateUrlProtocol: rejects bare hostnames without a TLD
 * (e.g. 'dfgdfg', 'localhost'), IP addresses, and any URL components beyond
 * a bare hostname (credentials, port, path, query, hash) to prevent
 * URL-confusion / phishing inputs like 'https://example.com@evil.com'.
 * @param {string} domain - Domain or URL to validate
 * @returns {boolean} - True only when input is security-safe and matches a bare-domain shape
 */
export function isValidDomain(domain) {
  return _isValidHostnameUrl(domain, { allowPath: false })
}

/**
 * Validate a full URL (path/query/hash allowed) with protocol checks and a real hostname (TLD required).
 * Use for privacy-policy and similar absolute links — stricter than validateUrlProtocol alone.
 * @param {string} url
 * @returns {boolean}
 */
export function isValidUrl(url) {
  return _isValidHostnameUrl(url, { allowPath: true })
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Validate that a string matches canonical UUID shape (8-4-4-4-12 hex segments).
 * @param {string} value
 * @returns {boolean}
 */
export function isValidUuid(value) {
  if (typeof value !== 'string' || !value.trim()) {
    return false
  }

  return UUID_REGEX.test(value)
}

/**
 * Normalize a validated domain to the bare hostname additional-domain fields store.
 * `validateUrlProtocol` may prepend `https://` internally so `new URL()` can parse;
 * that prefix is not written into the returned value.
 * @param {string} domain - Bare domain or URL with optional protocol (no path)
 * @returns {string|null} e.g. youtube.com, or null when not a valid domain
 */
export function normalizeDomainHost(domain) {
  if (!isValidDomain(domain)) {
    return null
  }

  const validation = validateUrlProtocol(domain)
  if (!validation.isValid || !validation.normalizedUrl) {
    return null
  }

  try {
    return new URL(validation.normalizedUrl).hostname
  } catch {
    return null
  }
}

/**
 * Normalize a validated primary domain to the absolute URL the API requires.
 * Always `https://` + hostname — the write path rejects a bare host.
 * @param {string} domain - Bare domain or URL with optional protocol (no path)
 * @returns {string|null} e.g. https://youtube.com, or null when not a valid domain
 */
export function normalizeDomainUrl(domain) {
  const hostname = normalizeDomainHost(domain)
  return hostname ? `https://${hostname}` : null
}

/**
 * Normalize a validated URL to the canonical absolute form the API expects.
 * Unlike normalizeDomainUrl, preserves path, search, and hash.
 * @param {string} url - Bare host/path or URL with optional protocol
 * @returns {string|null} e.g. https://example.com/privacy, or null when invalid
 */
export function normalizeUrl(url) {
  const validation = validateUrlProtocol(url)
  if (!validation.isValid || !validation.normalizedUrl) {
    return null
  }

  return validation.normalizedUrl
}

/**
 * Validate URL protocol to prevent dangerous protocols
 * @param {string} url - URL to validate
 * @returns {Object} - { isValid: boolean, normalizedUrl: string|null, error: string|null }
 */
export function validateUrlProtocol(url) {
  if (!url || typeof url !== 'string') {
    return {
      isValid: false,
      normalizedUrl: null,
      error: 'URL must be a non-empty string',
    }
  }

  // Normalize URL
  let normalizedUrl = url.trim()

  // Add https:// if no protocol specified
  if (!normalizedUrl.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:/)) {
    normalizedUrl = `https://${normalizedUrl}`
  }

  // Validate protocol using URL parsing
  try {
    const urlObj = new URL(normalizedUrl)
    const allowedProtocols = ['http:', 'https:']

    if (!allowedProtocols.includes(urlObj.protocol)) {
      log.error('validateUrlProtocol: Dangerous protocol blocked', {
        url,
        protocol: urlObj.protocol,
      })
      return {
        isValid: false,
        normalizedUrl: null,
        error: `Protocol ${urlObj.protocol} not allowed. Only http: and https: are permitted.`,
      }
    }

    return {
      isValid: true,
      normalizedUrl,
      error: null,
    }
  } catch (error) {
    log.error('validateUrlProtocol: Invalid URL format', { url, error })
    return {
      isValid: false,
      normalizedUrl: null,
      error: 'Invalid URL format',
    }
  }
}
